/* @jsx h */
import { defineComponent, h, computed } from "vue";

const AVATAR_COLORS = [
  "from-violet-500 to-purple-700",
  "from-blue-500 to-indigo-700",
  "from-emerald-500 to-teal-700",
  "from-amber-500 to-orange-700",
  "from-rose-500 to-pink-700",
  "from-cyan-500 to-blue-700",
  "from-lime-500 to-emerald-700",
  "from-fuchsia-500 to-violet-700",
];

export const avatarHashColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const avatarInitials = (name?: string | null, email?: string | null): string => {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email?.trim()) return email.trim().slice(0, 2).toUpperCase();
  return "?";
};

const SIZE = {
  sm: { wrap: "w-7 h-7",  text: "text-[10px]", badge: "w-3.5 h-3.5" },
  md: { wrap: "w-9 h-9",  text: "text-[11px]", badge: "w-4 h-4"   },
  lg: { wrap: "w-12 h-12", text: "text-sm",     badge: "w-5 h-5"   },
};

export type UserAvatarSize = keyof typeof SIZE;

export const UserAvatar = defineComponent<{
  name?: string | null;
  email?: string | null;
  size?: UserAvatarSize;
}>({
  name: "UserAvatar",
  props: ["name", "email", "size"],
  setup(props, { slots }) {
    const initials = computed(() => avatarInitials(props.name, props.email));
    const color = computed(() => avatarHashColor(props.email || props.name || "?"));
    const sz = computed(() => SIZE[props.size ?? "md"]);

    return () => (
      <div class="relative inline-flex flex-shrink-0">
        <div
          class={`${sz.value.wrap} ${sz.value.text} rounded-full bg-gradient-to-br ${color.value} flex items-center justify-center text-white font-bold shadow-sm`}
        >
          {initials.value}
        </div>
        {slots.badge && (
          <div class={`absolute -top-0.5 -right-0.5 ${sz.value.badge} rounded-full shadow ring-1 ring-base-200 overflow-hidden flex items-center justify-center`}>
            {slots.badge()}
          </div>
        )}
      </div>
    );
  },
});
